import type {
  ChartRange,
  EquityQuote,
  FundamentalData,
  ImpliedBeta,
  MarketMover,
  MarketMovers,
  OptionsChain,
  PriceHistory,
  PricePoint,
  SearchSuggestion,
} from '../types/finance'
import { apiGet } from './api-client'

const USE_MOCK = !import.meta.env.VITE_FINANCE_API_URL

// Tracks whether the most recent API call fell back to mock data
let _usingMockData = USE_MOCK
export function isUsingMockData(): boolean { return _usingMockData }

// ─── Mock Data ───────────────────────────────────────────────

function mockQuote(symbol: string): EquityQuote {
  const quotes: Record<string, Partial<EquityQuote>> = {
    AAPL: { name: 'Apple Inc.', currentPrice: 178.72, previousClose: 176.55, dailyVolume: 54230000, avgMonthlyVolume: 58400000, trailingBeta: 1.29, impliedBeta: 1.34, marketCap: 2780000000000, peRatio: 28.4, exchange: 'NASDAQ', nextEarningsDate: '2026-07-30' },
    MSFT: { name: 'Microsoft Corporation', currentPrice: 415.20, previousClose: 412.80, dailyVolume: 22100000, avgMonthlyVolume: 25600000, trailingBeta: 0.89, impliedBeta: 0.92, marketCap: 3090000000000, peRatio: 35.2, exchange: 'NASDAQ', nextEarningsDate: '2026-07-29' },
    GOOGL: { name: 'Alphabet Inc.', currentPrice: 155.30, previousClose: 153.90, dailyVolume: 28500000, avgMonthlyVolume: 26700000, trailingBeta: 1.06, impliedBeta: 1.12, marketCap: 1930000000000, peRatio: 24.1, exchange: 'NASDAQ', nextEarningsDate: '2026-07-23' },
    AMZN: { name: 'Amazon.com Inc.', currentPrice: 186.40, previousClose: 184.70, dailyVolume: 45600000, avgMonthlyVolume: 48200000, trailingBeta: 1.16, impliedBeta: 1.22, marketCap: 1940000000000, peRatio: 58.9, exchange: 'NASDAQ', nextEarningsDate: '2026-07-24' },
    TSLA: { name: 'Tesla Inc.', currentPrice: 248.50, previousClose: 242.80, dailyVolume: 98700000, avgMonthlyVolume: 112000000, trailingBeta: 2.31, impliedBeta: 2.45, marketCap: 792000000000, peRatio: 72.5, exchange: 'NASDAQ', nextEarningsDate: '2026-07-22' },
  }

  const base = quotes[symbol.toUpperCase()] || {
    name: `${symbol.toUpperCase()} Corp`,
    currentPrice: 100 + Math.random() * 200,
    previousClose: 100 + Math.random() * 200,
    dailyVolume: Math.floor(Math.random() * 50000000),
    avgMonthlyVolume: Math.floor(Math.random() * 60000000),
    trailingBeta: 0.5 + Math.random() * 2,
    impliedBeta: 0.5 + Math.random() * 2,
    marketCap: Math.floor(Math.random() * 500000000000),
    peRatio: 10 + Math.random() * 50,
    exchange: 'NYSE',
  }

  const price = base.currentPrice!
  const prevClose = base.previousClose!
  const nextEarningsDate = base.nextEarningsDate ?? null
  const daysUntilEarnings = nextEarningsDate
    ? Math.round((new Date(`${nextEarningsDate}T00:00:00`).getTime() - Date.now()) / 86400000)
    : null
  return {
    symbol: symbol.toUpperCase(),
    name: base.name!,
    currentPrice: price,
    previousClose: prevClose,
    change: +(price - prevClose).toFixed(2),
    changePercent: +(((price - prevClose) / prevClose) * 100).toFixed(2),
    dailyVolume: base.dailyVolume!,
    avgMonthlyVolume: base.avgMonthlyVolume!,
    trailingBeta: base.trailingBeta!,
    impliedBeta: base.impliedBeta!,
    marketCap: base.marketCap!,
    peRatio: base.peRatio!,
    currency: 'USD',
    exchange: base.exchange!,
    lastUpdated: new Date().toISOString(),
    dayHigh: +(Math.max(price, prevClose) * 1.008).toFixed(2),
    dayLow: +(Math.min(price, prevClose) * 0.992).toFixed(2),
    open: prevClose,
    nextEarningsDate,
    earningsDateEstimated: false,
    daysUntilEarnings,
  }
}

function mockFundamentals(symbol: string): FundamentalData {
  return {
    symbol: symbol.toUpperCase(),
    name: symbol.toUpperCase() === 'AAPL' ? 'Apple Inc.' : `${symbol.toUpperCase()} Corp`,
    sector: 'Technology',
    industry: 'Consumer Electronics',
    description: `${symbol.toUpperCase()} is a publicly traded company.`,
    marketCap: 2780000000000,
    enterpriseValue: 2850000000000,
    peRatio: 28.4,
    forwardPE: 26.1,
    pegRatio: 1.8,
    priceToBook: 46.2,
    priceToSales: 7.3,
    revenue: 383290000000,
    revenueGrowth: 0.028,
    netIncome: 97000000000,
    eps: 6.42,
    forwardEps: 6.85,
    profitMargin: 0.253,
    operatingMargin: 0.302,
    roe: 1.609,
    roa: 0.284,
    totalCash: 62480000000,
    totalDebt: 111090000000,
    debtToEquity: 181.34,
    currentRatio: 0.988,
    bookValue: 3.87,
    dividendYield: 0.0054,
    dividendRate: 0.96,
    payoutRatio: 0.1554,
    exDividendDate: '2026-02-07',
    fiftyTwoWeekHigh: 199.62,
    fiftyTwoWeekLow: 164.08,
    fiftyDayAvg: 174.30,
    twoHundredDayAvg: 182.15,
    avgVolume: 58400000,
    sharesOutstanding: 15550000000,
    floatShares: 15540000000,
    shortRatio: 1.82,
  }
}

function mockOptionsChain(symbol: string, expiration?: string): OptionsChain {
  const price = symbol.toUpperCase() === 'AAPL' ? 178.72 : 100 + Math.random() * 200
  const expirations = ['2026-03-07', '2026-03-14', '2026-03-21', '2026-03-28', '2026-04-04', '2026-04-17', '2026-05-15', '2026-06-19', '2026-09-18', '2026-12-18']
  const selected = expiration || expirations[0]

  const strikes = [-15, -10, -7.5, -5, -2.5, 0, 2.5, 5, 7.5, 10, 15].map((offset) => Math.round((price + offset) * 2) / 2)

  const calls = strikes.map((strike) => ({
    strike,
    lastPrice: +(Math.max(0, price - strike) + Math.random() * 3).toFixed(2),
    bid: +(Math.max(0, price - strike) + Math.random() * 2).toFixed(2),
    ask: +(Math.max(0, price - strike) + Math.random() * 2 + 0.1).toFixed(2),
    volume: Math.floor(Math.random() * 5000),
    openInterest: Math.floor(Math.random() * 20000),
    impliedVolatility: +(0.15 + Math.random() * 0.35).toFixed(4),
    inTheMoney: strike < price,
    contractSymbol: `${symbol.toUpperCase()}${selected.replace(/-/g, '')}C${String(strike * 1000).padStart(8, '0')}`,
    expiration: selected,
  }))

  const puts = strikes.map((strike) => ({
    strike,
    lastPrice: +(Math.max(0, strike - price) + Math.random() * 3).toFixed(2),
    bid: +(Math.max(0, strike - price) + Math.random() * 2).toFixed(2),
    ask: +(Math.max(0, strike - price) + Math.random() * 2 + 0.1).toFixed(2),
    volume: Math.floor(Math.random() * 5000),
    openInterest: Math.floor(Math.random() * 20000),
    impliedVolatility: +(0.15 + Math.random() * 0.35).toFixed(4),
    inTheMoney: strike > price,
    contractSymbol: `${symbol.toUpperCase()}${selected.replace(/-/g, '')}P${String(strike * 1000).padStart(8, '0')}`,
    expiration: selected,
  }))

  return { symbol: symbol.toUpperCase(), expirationDates: expirations, selectedExpiration: selected, calls, puts, underlyingPrice: price }
}

function mockImpliedBeta(symbol: string): ImpliedBeta {
  // rough, stable per-symbol mock so the panel renders without the live API
  const seed: Record<string, { rho: number; iv: number; tb: number }> = {
    AAPL: { rho: 0.48, iv: 29, tb: 1.09 }, MSFT: { rho: 0.62, iv: 24, tb: 0.89 }, GOOGL: { rho: 0.55, iv: 28, tb: 1.06 },
    AMZN: { rho: 0.58, iv: 33, tb: 1.16 }, TSLA: { rho: 0.58, iv: 46, tb: 2.31 }, NVDA: { rho: 0.66, iv: 38, tb: 2.20 },
  }
  const s = seed[symbol.toUpperCase()] || { rho: 0.5, iv: 30, tb: 1.0 }
  const mkt = 16.3
  const mk = (horizon: string, iv: number, exp: string) => {
    const ratio = +(iv / mkt).toFixed(3)
    return {
      horizon, stockExpiration: exp, marketExpiration: exp,
      stockImpliedVolPct: iv, marketImpliedVolPct: mkt,
      ivRatio: ratio, impliedBeta: +(s.rho * ratio).toFixed(3),
    }
  }
  const ts = [
    mk('nearest', s.iv + 3, '2026-07-01'),
    mk('~30d', s.iv, '2026-07-24'),
    mk('next-earnings', s.iv + 3, '2026-07-31'),
  ]
  return {
    symbol: symbol.toUpperCase(), market: 'SPY',
    method: 'ATM straddle IV ratio x 1y-daily correlation',
    correlation: s.rho, correlationWindow: '1y daily', spot: 0,
    trailingBeta: s.tb,
    impliedVsTrailing: +(ts[1].impliedBeta! - s.tb).toFixed(3),
    termStructure: ts, impliedBeta: ts[1].impliedBeta, note: null,
  }
}

const mockSuggestions: SearchSuggestion[] = [
  { symbol: 'AAPL', name: 'Apple Inc.', exchange: 'NASDAQ', type: 'Equity' },
  { symbol: 'MSFT', name: 'Microsoft Corporation', exchange: 'NASDAQ', type: 'Equity' },
  { symbol: 'GOOGL', name: 'Alphabet Inc.', exchange: 'NASDAQ', type: 'Equity' },
  { symbol: 'AMZN', name: 'Amazon.com Inc.', exchange: 'NASDAQ', type: 'Equity' },
  { symbol: 'TSLA', name: 'Tesla Inc.', exchange: 'NASDAQ', type: 'Equity' },
  { symbol: 'META', name: 'Meta Platforms Inc.', exchange: 'NASDAQ', type: 'Equity' },
  { symbol: 'NVDA', name: 'NVIDIA Corporation', exchange: 'NASDAQ', type: 'Equity' },
  { symbol: 'JPM', name: 'JPMorgan Chase & Co.', exchange: 'NYSE', type: 'Equity' },
  { symbol: 'V', name: 'Visa Inc.', exchange: 'NYSE', type: 'Equity' },
  { symbol: 'JNJ', name: 'Johnson & Johnson', exchange: 'NYSE', type: 'Equity' },
  { symbol: 'WMT', name: 'Walmart Inc.', exchange: 'NYSE', type: 'Equity' },
  { symbol: 'SPY', name: 'SPDR S&P 500 ETF', exchange: 'NYSE', type: 'ETF' },
  { symbol: 'QQQ', name: 'Invesco QQQ Trust', exchange: 'NASDAQ', type: 'ETF' },
]

// ─── API Functions ───────────────────────────────────────────

export async function searchSymbols(query: string): Promise<SearchSuggestion[]> {
  if (USE_MOCK) {
    _usingMockData = true
    await new Promise((r) => setTimeout(r, 200))
    const q = query.toUpperCase()
    return mockSuggestions.filter((s) => s.symbol.includes(q) || s.name.toUpperCase().includes(q)).slice(0, 8)
  }
  try {
    const result = await apiGet<SearchSuggestion[]>(`/api/finance/search?q=${encodeURIComponent(query)}`)
    _usingMockData = false
    return result
  } catch {
    console.warn('API search failed, falling back to mock data')
    _usingMockData = true
    const q = query.toUpperCase()
    return mockSuggestions.filter((s) => s.symbol.includes(q) || s.name.toUpperCase().includes(q)).slice(0, 8)
  }
}

export async function getQuote(symbol: string): Promise<EquityQuote> {
  if (USE_MOCK) {
    _usingMockData = true
    await new Promise((r) => setTimeout(r, 800))
    return mockQuote(symbol)
  }
  try {
    const result = await apiGet<EquityQuote>(`/api/finance/quote?symbol=${encodeURIComponent(symbol)}`)
    _usingMockData = false
    return result
  } catch {
    console.warn('API quote failed, falling back to mock data')
    _usingMockData = true
    return mockQuote(symbol)
  }
}

// ─── Price history (quote lightbox chart) ────────────────────
// No /api/finance/history route is deployed yet; until it ships the chart
// runs on a deterministic seeded walk anchored to the live quote price. The
// result's isMock flag (not the global banner flag) marks sample charts, so a
// missing chart route doesn't flag live quotes as sample data.

const RANGE_SPEC: Record<ChartRange, { apiRange: string; interval: string; points: number; stepMs: number }> = {
  '1D': { apiRange: '1d', interval: '5m', points: 78, stepMs: 5 * 60000 },
  '5D': { apiRange: '5d', interval: '30m', points: 65, stepMs: 30 * 60000 },
  '1M': { apiRange: '1mo', interval: '1d', points: 22, stepMs: 24 * 3600000 },
  '6M': { apiRange: '6mo', interval: '1d', points: 130, stepMs: 24 * 3600000 },
  '1Y': { apiRange: '1y', interval: '1d', points: 252, stepMs: 24 * 3600000 },
  '5Y': { apiRange: '5y', interval: '1wk', points: 260, stepMs: 7 * 24 * 3600000 },
  Max: { apiRange: 'max', interval: '1mo', points: 240, stepMs: 30 * 24 * 3600000 },
}

function historySeed(key: string): () => number {
  let h = 2166136261
  for (const ch of key) {
    h ^= ch.charCodeAt(0)
    h = Math.imul(h, 16777619)
  }
  let state = h >>> 0
  return () => {
    state = (state + 0x6d2b79f5) >>> 0
    let t = state
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function mockHistory(
  symbol: string,
  range: ChartRange,
  anchorPrice?: number,
  anchorPrevClose?: number | null
): PriceHistory {
  const spec = RANGE_SPEC[range]
  const rng = historySeed(`${symbol.toUpperCase()}:${range}`)
  const end = anchorPrice ?? 60 + rng() * 400
  // Per-step vol scaled to the sampling interval; drift so long ranges trend up.
  const stepYears = spec.stepMs / (365 * 24 * 3600000)
  const vol = 0.35 * Math.sqrt(stepYears)
  const drift = 0.07 * stepYears
  const closes = new Array<number>(spec.points)
  closes[spec.points - 1] = end
  for (let i = spec.points - 2; i >= 0; i--) {
    const shock = (rng() + rng() + rng() - 1.5) * 2 // ~N(0,1)-ish
    closes[i] = closes[i + 1] / (1 + drift + shock * vol)
  }
  // For 1D, pin the session open to the real previous close so the sample
  // intraday path agrees with the quote's displayed day change: shift the walk
  // by a linearly-decaying offset (start = prevClose, end unchanged).
  if (range === '1D' && anchorPrevClose != null && anchorPrevClose > 0) {
    const offset = anchorPrevClose - closes[0]
    const n = closes.length
    for (let i = 0; i < n; i++) closes[i] += offset * (1 - i / (n - 1))
  }
  const now = Date.now()
  const points: PricePoint[] = closes.map((c, i) => ({
    t: new Date(now - (spec.points - 1 - i) * spec.stepMs).toISOString(),
    close: +c.toFixed(2),
  }))
  const prevClose =
    range === '1D'
      ? anchorPrevClose != null && anchorPrevClose > 0
        ? +anchorPrevClose.toFixed(2)
        : +(closes[0] * (0.996 + rng() * 0.008)).toFixed(2)
      : null
  return {
    symbol: symbol.toUpperCase(),
    range,
    interval: spec.interval,
    points,
    previousClose: prevClose,
    isMock: true,
  }
}

export async function getPriceHistory(
  symbol: string,
  range: ChartRange,
  anchorPrice?: number,
  anchorPrevClose?: number | null
): Promise<PriceHistory> {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 350))
    return mockHistory(symbol, range, anchorPrice, anchorPrevClose)
  }
  try {
    const spec = RANGE_SPEC[range]
    const result = await apiGet<PriceHistory>(
      `/api/finance/history?symbol=${encodeURIComponent(symbol)}&range=${spec.apiRange}`
    )
    if (!Array.isArray(result.points) || result.points.length < 2) throw new Error('empty history')
    return { ...result, range, isMock: false }
  } catch {
    return mockHistory(symbol, range, anchorPrice, anchorPrevClose)
  }
}

// ─── Market movers ───────────────────────────────────────────
// No dedicated backend route yet, so after trying /api/finance/movers the
// client derives movers by ranking live quotes for a liquid watch universe.

const MOVERS_UNIVERSE: { symbol: string; name: string }[] = [
  { symbol: 'AAPL', name: 'Apple Inc.' },
  { symbol: 'MSFT', name: 'Microsoft Corporation' },
  { symbol: 'NVDA', name: 'NVIDIA Corporation' },
  { symbol: 'GOOGL', name: 'Alphabet Inc.' },
  { symbol: 'AMZN', name: 'Amazon.com Inc.' },
  { symbol: 'META', name: 'Meta Platforms Inc.' },
  { symbol: 'TSLA', name: 'Tesla Inc.' },
  { symbol: 'AVGO', name: 'Broadcom Inc.' },
  { symbol: 'AMD', name: 'Advanced Micro Devices' },
  { symbol: 'NFLX', name: 'Netflix Inc.' },
  { symbol: 'JPM', name: 'JPMorgan Chase & Co.' },
  { symbol: 'GS', name: 'Goldman Sachs Group' },
  { symbol: 'XOM', name: 'Exxon Mobil' },
  { symbol: 'LLY', name: 'Eli Lilly and Company' },
  { symbol: 'MRNA', name: 'Moderna Inc.' },
  { symbol: 'BA', name: 'The Boeing Company' },
  { symbol: 'PLTR', name: 'Palantir Technologies' },
  { symbol: 'MU', name: 'Micron Technology' },
  { symbol: 'CCL', name: 'Carnival Corporation' },
  { symbol: 'FCX', name: 'Freeport-McMoRan' },
]

function mockMovers(): MarketMovers {
  // Deterministic per calendar day so the card is stable across renders.
  const day = new Date().toISOString().slice(0, 10)
  const seeded = (s: string) => {
    let h = 2166136261
    for (const ch of `${day}:${s}`) {
      h ^= ch.charCodeAt(0)
      h = Math.imul(h, 16777619)
    }
    return ((h >>> 0) % 10000) / 10000
  }
  const rows: MarketMover[] = MOVERS_UNIVERSE.map((e) => {
    const pct = (seeded(e.symbol) - 0.5) * 12
    const price = 40 + seeded(`${e.symbol}:p`) * 400
    return {
      symbol: e.symbol,
      name: e.name,
      price: +price.toFixed(2),
      change: +((price * pct) / 100).toFixed(2),
      changePercent: +pct.toFixed(2),
      volume: Math.floor(seeded(`${e.symbol}:v`) * 80000000),
    }
  })
  const sorted = [...rows].sort((a, b) => b.changePercent - a.changePercent)
  return {
    asOf: new Date().toISOString(),
    source: 'mock',
    universe: 'Sample universe',
    gainers: sorted.slice(0, 5),
    losers: sorted.slice(-5).reverse(),
  }
}

async function deriveMoversFromQuotes(): Promise<MarketMovers> {
  const settled = await Promise.allSettled(
    MOVERS_UNIVERSE.map((e) => apiGet<EquityQuote>(`/api/finance/quote?symbol=${encodeURIComponent(e.symbol)}`))
  )
  const rows: MarketMover[] = []
  settled.forEach((s, i) => {
    if (s.status !== 'fulfilled') return
    const q = s.value
    rows.push({
      symbol: q.symbol,
      name: q.name || MOVERS_UNIVERSE[i].name,
      price: q.currentPrice,
      change: q.change,
      changePercent: q.changePercent,
      volume: q.dailyVolume ?? null,
    })
  })
  if (rows.length < 6) throw new Error('too few live quotes to derive movers')
  const sorted = [...rows].sort((a, b) => b.changePercent - a.changePercent)
  return {
    asOf: new Date().toISOString(),
    source: 'derived',
    universe: `${rows.length} liquid large caps`,
    gainers: sorted.slice(0, 5),
    losers: sorted.slice(-5).reverse(),
  }
}

export async function getMarketMovers(): Promise<MarketMovers> {
  if (USE_MOCK) {
    _usingMockData = true
    await new Promise((r) => setTimeout(r, 500))
    return mockMovers()
  }
  try {
    const result = await apiGet<MarketMovers>('/api/finance/movers')
    _usingMockData = false
    return result
  } catch {
    // Movers route not deployed yet — rank live quotes instead.
  }
  try {
    const result = await deriveMoversFromQuotes()
    _usingMockData = false
    return result
  } catch {
    console.warn('Deriving movers from quotes failed, falling back to mock data')
    _usingMockData = true
    return mockMovers()
  }
}

export async function getFundamentals(symbol: string): Promise<FundamentalData> {
  if (USE_MOCK) {
    _usingMockData = true
    await new Promise((r) => setTimeout(r, 1000))
    return mockFundamentals(symbol)
  }
  try {
    const result = await apiGet<FundamentalData>(`/api/finance/fundamentals?symbol=${encodeURIComponent(symbol)}`)
    _usingMockData = false
    return result
  } catch {
    console.warn('API fundamentals failed, falling back to mock data')
    _usingMockData = true
    return mockFundamentals(symbol)
  }
}

export async function getImpliedBeta(symbol: string): Promise<ImpliedBeta> {
  if (USE_MOCK) {
    _usingMockData = true
    await new Promise((r) => setTimeout(r, 600))
    return mockImpliedBeta(symbol)
  }
  try {
    const result = await apiGet<ImpliedBeta>(`/api/finance/impliedbeta?symbol=${encodeURIComponent(symbol)}`)
    _usingMockData = false
    return result
  } catch {
    console.warn('API implied beta failed, falling back to mock data')
    _usingMockData = true
    return mockImpliedBeta(symbol)
  }
}

export async function getOptionsChain(symbol: string, expiration?: string): Promise<OptionsChain> {
  if (USE_MOCK) {
    _usingMockData = true
    await new Promise((r) => setTimeout(r, 1000))
    return mockOptionsChain(symbol, expiration)
  }
  try {
    const params = new URLSearchParams({ symbol })
    if (expiration) params.set('expiration', expiration)
    const result = await apiGet<OptionsChain>(`/api/finance/options?${params}`)
    _usingMockData = false
    return result
  } catch {
    console.warn('API options failed, falling back to mock data')
    _usingMockData = true
    return mockOptionsChain(symbol, expiration)
  }
}
